import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  ChevronRight,
  Clock,
  Search,
  Filter,
  Terminal,
  Database,
  Server,
  Globe,
  Loader2
} from 'lucide-react';
import { topicService } from '../services/topicService';
import { Topic } from '../types/topic';
import PageHeader from '../components/common/PageHeader';

const Learn: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [topics, setTopics] = useState<Topic[]>([]);
  const [popularTopics, setPopularTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const categoryIcons = {
    'software-engineering': Server,
    'devops': Terminal,
    'databases': Database,
    'fundamentals': Globe
  };

  // Fetch topics from API
  const fetchTopics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await topicService.getAll({
        page: 'learn', // Use page context - backend maps to learn categories
        category: selectedCategory === 'all'
          ? undefined  // Use page default categories
          : selectedCategory, // Override with selected category
        search: searchTerm.trim() || undefined,
        limit: 50, // Get more topics for client-side filtering if needed
        pageNumber: 1,
      });

      setTopics(response.topics);
      setPagination({
        total: response.total,
        page: response.page,
        totalPages: response.totalPages,
        hasNext: response.hasNext,
        hasPrev: response.hasPrev,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load topics');
      console.error('Error fetching topics:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchTerm]);

  // Fetch popular topics
  const fetchPopularTopics = useCallback(async () => {
    try {
      const popular = await topicService.getPopular(3);
      setPopularTopics(popular);
    } catch (err) {
      console.error('Error fetching popular topics:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchTopics();
    fetchPopularTopics();
  }, [fetchTopics, fetchPopularTopics]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim() || selectedCategory !== 'all') {
        fetchTopics();
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedCategory, fetchTopics]);

  const getTopicLink = (topic: Topic) => {
    // Use topic.link if available, otherwise construct from slug
    return topic.link || `/learn/${topic.slug}`;
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <PageHeader
          title="Learn"
          description="Master essential technologies, design principles, and tools used in modern software development"
          icon={BookOpen}
          iconColor="text-green-600 dark:text-green-400"
          gradientColors={{
            from: 'from-green-500',
            via: 'via-emerald-500',
            to: 'to-teal-500'
          }}
        />

        {/* Search and Filters */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input-field pl-10 pr-8 appearance-none"
              >
                <option value="all">All Categories</option>
                <option value="software-engineering">Software Engineering</option>
                <option value="devops">DevOps</option>
                <option value="databases">Databases</option>
                <option value="fundamentals">Fundamentals</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-green-600 dark:text-green-400 mr-3" />
            <span className="text-gray-600 dark:text-gray-400">Loading topics...</span>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Error Loading Topics
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error}
            </p>
            <button
              onClick={() => fetchTopics()}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Topics Grid */}
        {!loading && !error && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {topics.map((topic) => {
                const CategoryIcon = categoryIcons[topic.category as keyof typeof categoryIcons] || BookOpen;

                return (
                  <Link
                    key={topic.id}
                    to={getTopicLink(topic)}
                    className="group card p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  >
                    {/* Topic Header */}
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl flex items-center justify-center text-3xl">
                        {topic.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                          {topic.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          {topic.description}
                        </p>
                      </div>
                    </div>

                    {/* Progress - Can be calculated from user progress later */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Progress</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          0%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="h-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                          style={{ width: '0%' }}
                        ></div>
                      </div>
                    </div>

                    {/* Topic Details */}
                    <div className="mb-6">
                      <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {topic.subtopicsCount > 0 && (
                          <div className="flex items-center space-x-1">
                            <BookOpen className="w-4 h-4" />
                            <span>{topic.subtopicsCount} modules</span>
                          </div>
                        )}
                        {topic.estimatedTime && (
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{topic.estimatedTime}</span>
                          </div>
                        )}
                      </div>

                      {/* Prerequisites */}
                      {topic.prerequisites && topic.prerequisites.length > 0 && (
                        <div className="mb-3">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Prerequisites:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {topic.prerequisites.slice(0, 2).map((prereq: string, idx: number) => (
                              <span key={idx} className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400">
                                {prereq}
                              </span>
                            ))}
                            {topic.prerequisites.length > 2 && (
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                +{topic.prerequisites.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Category and Action */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CategoryIcon className="w-4 h-4 text-gray-400" />
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${topic.difficulty === 'beginner' ? 'badge-green' :
                          topic.difficulty === 'intermediate' ? 'badge-yellow' :
                            'badge-red'
                          }`}>
                          {topic.difficulty.charAt(0).toUpperCase() + topic.difficulty.slice(1)}
                        </span>
                      </div>
                      <div className="flex items-center text-primary-600 dark:text-primary-400 group-hover:translate-x-1 transition-transform">
                        <span className="text-sm font-medium mr-1">Learn Now</span>
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* No Results */}
            {topics.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  No topics found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Try adjusting your search or filter criteria
                </p>
              </div>
            )}
          </>
        )}

        {/* Popular Topics */}
        {popularTopics.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8 text-center">
              Most Popular Topics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {popularTopics.map((topic) => (
                <Link
                  key={topic.id}
                  to={getTopicLink(topic)}
                  className="card p-6 text-center hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="text-4xl mb-3">{topic.icon}</div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {topic.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {topic.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 card p-8 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700">
          <div className="text-center">
            <BookOpen className="w-16 h-16 text-green-600 dark:text-green-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Ready to Start Learning?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
              Explore our comprehensive learning paths and master essential technologies, design principles,
              and tools used in modern software development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/system-design" className="btn-primary">
                Explore System Design
              </Link>
              <Link to="/practice" className="btn-secondary">
                Start Practicing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Learn;
